import { get } from 'lodash';

import { variablesService } from 'services/variables';
import { localizationService } from '../localization';
import { GeocodeAddressComponent, GeocodeResponse, Coordinates } from './types';
import { formatString } from '../utils';

export const DEFAULT_MAP_CENTER = { lat: 34.144308, lng: -118.803909 };
export const DEFAULT_ZOOM_LVL = 9;
export const DEFAULT_MARKER_SIZE = 25;

export const OK_RESULTS_STATUS_CODE = 'OK';

export const DEFAULT_MAP_OPTIONS = {
  disableDefaultUI: false,
  scrollwheel: false,
  draggable: true,
  clickable: true,
  disableDoubleClickZoom: false,
  gestureHandling: 'cooperative'
};

export const ERROR_MESSAGE = 'Unable to get geocode info for address: {0}';

const COUNTRY_LEVEL = 'country';
const DEFAULT_COUNTRY = 'US';

export const getApiKey = (): string => variablesService.getItem('googleMapsKey') || '';

export const getCoordinates = (addressInfo: GeocodeResponse): Coordinates =>
  get(addressInfo, 'results[0].geometry.location', DEFAULT_MAP_CENTER);

export const getAddress = (addressInfo: GeocodeResponse, defaultValue: string): string =>
  get(addressInfo, 'results[0].formatted_address', defaultValue);

export const getCountryNameByPostalCode = (addressInfo: GeocodeResponse, postalCode: string): string => {
  let matchedResult = addressInfo.results.find(result => result.formatted_address.includes(postalCode));
  if (!matchedResult && addressInfo.results.length === 1) matchedResult = addressInfo.results[0];
  const addressComponents = get(matchedResult, 'address_components', []);
  const countryLevel = addressComponents.find((level: GeocodeAddressComponent) => level.types.includes(COUNTRY_LEVEL));

  return get(countryLevel, 'short_name', DEFAULT_COUNTRY);
};

/**
 * Ensures the Google Maps JS SDK is loaded. Loads it on demand if not present.
 */
let sdkLoadPromise: Promise<void> | null = null;
const ensureGoogleMapsLoaded = (): Promise<void> => {
  const googleMaps = (window as any).google;
  if (googleMaps?.maps?.Geocoder) {
    return Promise.resolve();
  }

  const apiKey = getApiKey().trim();
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is missing'));
  }

  if (!sdkLoadPromise) {
    sdkLoadPromise = new Promise<void>((resolve, reject) => {
      const language = localizationService.getLanguage();
      const queryParams = new URLSearchParams({
        key: apiKey,
        libraries: 'geocoding',
        language
      });
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${queryParams.toString()}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        sdkLoadPromise = null;
        reject(new Error('Failed to load Google Maps JS SDK'));
      };
      document.head.appendChild(script);
    });
  }
  return sdkLoadPromise;
};

/**
 * Geocodes an address using the Maps JavaScript SDK (google.maps.Geocoder).
 * This avoids direct requests to the Geocoding REST API which are blocked
 * when HTTP-referrer restrictions are set on the API key.
 */
export const getAddressInfo = async (address: string, country?: string): Promise<GeocodeResponse> => {
  await ensureGoogleMapsLoaded();
  const googleMaps = window.google;
  const geocoder = new googleMaps.maps.Geocoder();
  const request: google.maps.GeocoderRequest = {
    address,
    ...(country ? { componentRestrictions: { country } } : {})
  };
  return new Promise<GeocodeResponse>((resolve, reject) => {
    geocoder.geocode(request, (results, status) => {
      const statusStr = status.toString();
      if (statusStr === 'OK') {
        // Normalize geometry.location: the JS SDK returns a google.maps.LatLng
        // (method-based), but all consumers expect a plain { lat, lng } literal.
        // Guard for plain objects too (e.g. in tests).
        const normalized = (results || []).map(result => {
          const loc = result.geometry.location;
          const lat = typeof loc.lat === 'function' ? loc.lat() : (loc as any).lat;
          const lng = typeof loc.lng === 'function' ? loc.lng() : (loc as any).lng;
          return {
            ...result,
            geometry: { ...result.geometry, location: { lat, lng } }
          };
        });
        resolve({ results: normalized as unknown as google.maps.GeocoderResult[], status: 'OK' });
      } else if (statusStr === 'ZERO_RESULTS') {
        resolve({ results: [], status: 'ZERO_RESULTS' });
      } else {
        reject(new Error(formatString(ERROR_MESSAGE, address).join('')));
      }
    });
  });
};

/**
 * receive origins and destination coordinates returns distance between them
 * @param lat1 {number}
 * @param lon1 {number}
 * @param lat2 {number}
 * @param lon2 {number}
 * @returns {number}
 */
export const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  if (lat1 === lat2 && lon1 === lon2) {
    return 0;
  }
  const radLat1 = (Math.PI * lat1) / 180;
  const radLat2 = (Math.PI * lat2) / 180;
  const theta = lon1 - lon2;
  const radTheta = (Math.PI * theta) / 180;
  let dist = Math.sin(radLat1) * Math.sin(radLat2) + Math.cos(radLat1) * Math.cos(radLat2) * Math.cos(radTheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  return dist;
};

export const convertMilesToKilometers = (miles: number): number => miles * 1.60934;
export const convertKilometersToMiles = (kilometers: number): number => kilometers / 1.60934;
